"use client";

import clsx from "clsx";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Icon, { ICON_SIZES } from "@/components/library/Icon";
import Tooltip from "@/components/library/Tooltip";
import { extractTableAsTsv, getScrollFadeMask, writeClipboardMulti } from "./utils";
import { TableHoverProvider } from "./TableHoverContext";

export const MarkdownTable: React.FC<
  React.HTMLAttributes<HTMLTableElement> & { children?: React.ReactNode }
> = (props) => {
  const tableRef = useRef<HTMLTableElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [scrollState, setScrollState] = useState({ canScrollLeft: false, canScrollRight: false });
  const { t: ct } = useTranslation("common");

  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;
    setScrollState({
      canScrollLeft: el.scrollLeft > 0,
      canScrollRight: el.scrollLeft < el.scrollWidth - el.clientWidth - 3,
    });
  };

  useEffect(() => {
    updateScrollState();
    // Expanding/collapsing changes the scrollable width, so recompute the fade.
  }, [expanded]);

  const onCopyTable = () => {
    const tsv = extractTableAsTsv(tableRef.current);
    if (!tsv) return;
    writeClipboardMulti(tsv).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const { children, ...rest } = props;
  const expandLabel = expanded ? ct("collapseTable") : ct("expandTable");

  return (
    <div className={clsx("md-table-block relative w-full min-w-0 group", expanded && "expanded")}>
      <div
        ref={scrollRef}
        className="overflow-x-auto w-full"
        onScroll={updateScrollState}
        style={getScrollFadeMask(scrollState.canScrollLeft, scrollState.canScrollRight)}
      >
        <TableHoverProvider>
          <table ref={tableRef} className="relative" {...rest}>
            {children}
          </table>
        </TableHoverProvider>
      </div>
      <div className="copy-table-overlay absolute top-1 right-1 z-10 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-100 pointer-events-none group-hover:pointer-events-auto">
        <Tooltip content={expandLabel} className="min-w-max">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-label={expandLabel}
            aria-pressed={expanded}
            className="flex items-center px-2 py-1 rounded-md bg-panel/90 backdrop-blur-sm border border-300 text-500 hover:text-700 hover:bg-panel shadow-sm text-xs transition-colors"
          >
            <Icon
              icon={expanded ? "down-left-and-up-right-to-center" : "up-right-and-down-left-from-center"}
              size={ICON_SIZES.XS}
            />
          </button>
        </Tooltip>
        <button
          type="button"
          onClick={onCopyTable}
          className="flex items-center gap-1 px-2 py-1 rounded-md bg-panel/90 backdrop-blur-sm border border-300 text-500 hover:text-700 hover:bg-panel shadow-sm text-xs transition-colors"
        >
          <Icon icon="copy" size={ICON_SIZES.XS} />
          {copied && <span>{ct("copied")}</span>}
        </button>
      </div>
    </div>
  );
};
